import React from "react"
import { Option } from "../types"
import { makeTreeGridProjectRow, makeTreegridProjectRows } from "./rows"
import { TimeZoneType } from "../../constants/timezones"
import { ProjectColumnName, TreeGridProject } from "./types"
import { makeTreegridLayout } from "./layout"
import {
  createTreegrid,
  destroyTreegrid,
  getSummaryTextWithRowCount,
  syncDataFromServerToGrid,
  updateGridData,
} from "../utils/tree-grid"
import { useTreeGridFilters } from "../hooks/use-treegrid-filters"
import {
  getColorFromColorHtml,
  getShouldRemoveProjectRowBasedOnContext,
  getStateFromStateIcon,
  mapGroupByToColumns,
  mapTranslationsToColor,
  mapTranslationsToGroupBy,
} from "../utils"
import { DateTimeService } from "../../services/date-time-service"
import { useRouter } from "../../hooks"
import { useUrlWithContext } from "../../hooks/use-url-with-context"
import { useTranslations } from "../hooks/use-translations"
import { ProjectsViewOptions } from "../../projects/components/ProjectsViewSelect"
import { useUpdateProjectStatus } from "../../projects/hooks/use-update-project-status"
import { useAppContext } from "../../app-context/use-app-context"
import { useContextOptions } from "../../context-options/hooks/use-context-options"
import { paths } from "../../paths"
import { useProjectMutations } from "../../projects/hooks/use-project-mutations"
import { ProjectViewModel } from "../../projects/api/project"
import { getMaxDate, getMinDate } from "../../utils"
import { GroupBy } from "../../types/common"
import { SystemStatus } from "../../lib/system-status"
import { HolidayViewModel } from "../../holidays/api/holiday"
import { useSnackbar } from "notistack"
import { useTranslation } from "react-i18next"
import { makeOnSearchChange } from "../base/event-handlers/on-search-change"
import { makeOnFilterFinish } from "../base/event-handlers/on-filter-finish"
import { getGanttVisibleRange } from "../utils/gantt-utils"
import { makeOnAfterSectionResize } from "../base/event-handlers/on-after-section-resize"
import { CalendarSettingsViewModel } from "../../calendar-settings/api/calendar-settings"
import { makeCalendarSettingsId } from "../../calendar-settings/api/calendar-settings-id"
import { useCanEditDateByCalendarSettingsId } from "../hooks/use-can-edit-date-by-calendar-settings-id"
import { useFileDownload } from "../../storage/hooks/use-file-download"

const id = "__treegrid_project_list__"

export const useProjectList = (props: ProjectListProps) => {
  // TODO: Slowly revert the old translations to new translations
  const translations = useTranslations()
  const treegridTranslations = useTranslation("treegrid").t
  const projectTranslations = useTranslation("project").t
  const { t: commonTranslations } = useTranslation("common")
  const { initProjectUpdate } = useProjectMutations()
  const { history, ...router } = useRouter()
  const [navigateTo, setNavigateTo] = React.useState<string | null>(null)
  const { createPathWithContext } = useUrlWithContext()
  const { appContext } = useAppContext()
  const { userMembershipContextOptions } = useContextOptions()
  const { showFilters, toggleFilters } = useTreeGridFilters({ gridId: id })
  const { updateProjectStatus, updatedProject } = useUpdateProjectStatus()
  const zoomRef = React.useRef<string | null>(null)
  const query = router.query as { view?: ProjectsViewOptions }
  const projectsView = query.view || "currentAndFuture"
  const { enqueueSnackbar } = useSnackbar()
  const { openFileInBrowser } = useFileDownload()

  const toolbarSummaryText = getSummaryTextWithRowCount(
    translations.toolbarProjectListSummaryText,
    props.projects.length
  )
  translations.toolbarProjectListSummaryText = toolbarSummaryText

  const applyWeekNumberPrefixLabel = (t: (key: string) => string) => {
    const label = t("week.short") + " "
    document.documentElement.style.setProperty("--week-label", `"${label}"`)
  }

  // Set the Week number prefix in calendar to the correct translation on initial load
  React.useEffect(() => {
    applyWeekNumberPrefixLabel(commonTranslations)
  }, [commonTranslations])

  React.useEffect(() => {
    if (navigateTo) history.push(createPathWithContext(navigateTo))
    return () => setNavigateTo(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateTo])

  React.useEffect(() => {
    const grid = window.Grids[id]
    if (grid && updatedProject) {
      // PRESERVE LINKS AND FILES FROM ORIGINAL PROJECT
      const enrichedProject = getEnrichedProjects([updatedProject], props.projects)[0]
      const shouldRemoveRow = getShouldRemoveRow(enrichedProject, projectsView)
      if (shouldRemoveRow) {
        const row = grid.GetRowById(updatedProject.id)
        if (row) grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
      } else {
        const projectRow = makeTreeGridProjectRow({ project: enrichedProject, translations, ...props })
        syncDataFromServerToGrid(grid, [projectRow])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProject])

  // Adding keydown listener to handle ctrl/cmd + c and ctrl/cmd + v for copy paste functionality,
  // and stopping the event propagation so that it doesn't trigger any other copy paste handlers
  // in the app when the treegrid is focused.
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "v")) {
        const grid = window.Grids[id]
        if (grid) {
          e.stopImmediatePropagation()
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown, true) // true = capture phase
    return () => document.removeEventListener("keydown", handleKeyDown, true)
  }, [])

  const showGantt = projectsView === "currentAndFuture" || projectsView === "all"
  const showGroupBy = appContext.mainContext?.type === "org"
  const allPlannedStartDates = props.projects.map((project) => project.plannedStartDate).filter(Boolean)
  const allPlannedEndDates = props.projects.map((project) => project.plannedEndDate).filter(Boolean)
  const allActualStartDates = props.projects.map((project) => project.actualStartDate).filter(Boolean)
  const allActualEndDates = props.projects.map((project) => project.actualEndDate).filter(Boolean)
  const allDates = allPlannedStartDates.concat(allPlannedEndDates).concat(allActualStartDates).concat(allActualEndDates)
  const minStartDate = getMinDate(allDates)
  const maxEndDate = getMaxDate(allDates)

  const layout = makeTreegridLayout({
    id,
    showFilters,
    translations,
    showGantt,
    showGanttToolbarActions: showGantt,
    groupBy: GroupBy.NO_GROUPING,
    projectsView,
    showGroupBy,
    ...props,
    holidays: props.holidays || [],
    ganttZoomLeftEdgeRefDate: minStartDate || new Date(),
    ganttZoomRightEdgeRefDate: maxEndDate || new Date(),
  })

  const canEditDateByCalendarSettingsId = useCanEditDateByCalendarSettingsId(props.calendarSettingsById)
  const defaultGanttZoomRange = getGanttVisibleRange({
    startDate: new Date(),
    endDate: maxEndDate,
  })

  // @ts-ignore
  window.Grids["OnFilterToggle"] = function () {
    toggleFilters()
  }

  // Disable editing of weekends and holidays based on the project's/organization's settings
  window.Grids.OnCanEditDate = function (grid, row, col, date) {
    const projectCalendarSettingsId = makeCalendarSettingsId({ type: "project", id: row.id })
    const canEditDate = canEditDateByCalendarSettingsId[projectCalendarSettingsId]
    if (canEditDate) return canEditDate(date)

    return true // If there are no calendar settings for the project, allow editing by default
  }

  window.Grids.OnValueChanged = function (
    grid: TGrid,
    row: TRow & TreeGridProject,
    column: ProjectColumnName,
    newValue,
    oldValue
  ) {
    // NOTE: Returning early from this function will cause unexpected behavior in the treegrid
    const isColumnEditAllowed = (row as any)[`${column}CanEdit`] === 1
    const hasValueChanged = newValue !== oldValue
    const shouldDispatchUpdateRequest = isColumnEditAllowed && hasValueChanged
    if (shouldDispatchUpdateRequest) {
      let value = adaptValue({ column, newValue, oldValue })

      // If the column is a date column, then we need to adjust the timezone
      if (isDateColumn(column) && value) {
        const dateTimeService = new DateTimeService({
          dateFormat: props.dateFormat,
          timeZone: props.timeZone,
          enableTimeComponent: row.enableTimeComponent,
        })
        const date = new Date(value)
        const adjustedDate = dateTimeService.addTimezoneOffset(date, "UTC")
        value = adjustedDate.toISOString()
      }
      if (column === "plannedStartDate" || column === "plannedEndDate") {
        const startRaw = column === "plannedStartDate" ? value : row.plannedStartDate
        const endRaw = column === "plannedEndDate" ? value : row.plannedEndDateOrig ?? row.plannedEndDate
        if (startRaw && endRaw) {
          const start = new Date(startRaw)
          const end = new Date(endRaw)
          // start date cannot be after end date
          if (end < start) {
            enqueueSnackbar(treegridTranslations("startDateAfterEndDateError"), { variant: "error" })
            value = new Date(oldValue).toISOString()
          }
          // If the start or end date is invalid, we should revert back the update
          else if (start && end && !DateTimeService.validateProjectDuration(start, end)) {
            enqueueSnackbar(treegridTranslations("projectDurationError"), { variant: "error" })
            value = new Date(oldValue).toISOString()
          }
        }
      }

      if (column === "ganttBarColor") {
        row.GanttGanttClass = value === "Default" ? "Blue" : value // changes the color of the gantt bar immediately
        value = mapTranslationsToColor(translations)[value]
      }

      if (column === "status") {
        const [mainStatusId, subStatusId] = newValue.toString().split("_")
        const status = SystemStatus.statusByIndex[parseInt(mainStatusId)]
        updateProjectStatus(row.id, { status, subStatusId: subStatusId ?? null })
      }

      if (column === "actualStartDate" || column === "actualEndDate") {
        const startRaw = column === "actualStartDate" ? value : row.actualStartDate
        const endRaw = column === "actualEndDate" ? value : row.actualEndDate
        let sendReq = true
        // actual start and end dates cannot be in the future
        if (startRaw || endRaw) {
          // The column being edited comes from TreeGrid in local timezone, needs local conversion
          // The other column comes from server already as UTC ISO, use as-is
          const startISO = startRaw
            ? column === "actualStartDate"
              ? DateTimeService.toLocalISOStringFormat(new Date(startRaw))
              : new Date(startRaw).toISOString()
            : ""
          const endISO = endRaw
            ? column === "actualEndDate"
              ? DateTimeService.toLocalISOStringFormat(new Date(endRaw))
              : new Date(endRaw).toISOString()
            : ""
          const nowISO = row.enableTimeComponent
            ? DateTimeService.toLocalISOStringFormat(new Date())
            : new Date().toISOString()

          // actual start and end dates cannot be in the future
          if (column === "actualStartDate" && startISO && startISO > nowISO) {
            enqueueSnackbar(projectTranslations("projectDetails.actualStartDateCannotBeInFuture"), {
              variant: "error",
            })
            value = oldValue === "" ? "" : new Date(oldValue).toISOString()
            sendReq = false
          } else if (column === "actualEndDate" && endISO && endISO > nowISO) {
            enqueueSnackbar(projectTranslations("projectDetails.actualEndDateCannotBeInFuture"), { variant: "error" })
            value = oldValue === "" ? "" : new Date(oldValue).toISOString()
            sendReq = false
          }
          // actual start date cannot be after actual end date and vice versa
          else if (startISO && endISO) {
            const startCompare = row.enableTimeComponent ? startISO : startISO.substring(0, 10)
            const endCompare = row.enableTimeComponent ? endISO : endISO.substring(0, 10)

            if (column === "actualEndDate" && endCompare < startCompare) {
              enqueueSnackbar(projectTranslations("projectDetails.actualEndCannotBeBeforeStartError"), {
                variant: "error",
              })
              value = oldValue === "" ? "" : new Date(oldValue).toISOString()
              sendReq = false
            }
            if (column === "actualStartDate" && startCompare > endCompare) {
              enqueueSnackbar(projectTranslations("projectDetails.actualStartCannotBeAfterEndError"), {
                variant: "error",
              })
              value = oldValue === "" ? "" : new Date(oldValue).toISOString()
              sendReq = false
            }
          }
        }
        // If the value is valid and has passed all the validations,
        // then we can send the update request to the server. If not,
        // we should revert back the value in the grid and not send the request.
        if (sendReq) {
          updateProjectStatus(row.id, { [column]: value })
          return
        }
        return value ? new Date(value) : ""
      }

      // Special handling for multi-select columns to change the value structure
      if (
        column === "managers" ||
        column === "customers" ||
        column === "participants" ||
        column === "suppliers" ||
        column === "workspaces"
      ) {
        const oldIds: string[] = oldValue.split(";").filter(Boolean)
        const newIds: string[] = newValue.split(";").filter(Boolean)

        const add = newIds
          .filter((id) => !oldIds.includes(id))
          .map((id) => {
            const option = props.options.find((opt) => opt.id === id)
            return { id, name: option?.name ?? "" }
          })

        const remove = oldIds
          .filter((id) => !newIds.includes(id))
          .map((id) => {
            const option = props.options.find((opt) => opt.id === id)
            return { id, name: option?.name ?? "" }
          })

        value = { add, remove }
      }

      initProjectUpdate(row.id, { field: column as any, value }).then((projectResponse) => {
        if (!projectResponse || !grid) return
        const project = "project" in projectResponse ? projectResponse.project : projectResponse
        // PRESERVE LINKS AND FILES FROM ORIGINAL PROJECT
        const enrichedProject = getEnrichedProjects([project], props.projects)[0]
        const projectRow = makeTreeGridProjectRow({ project: enrichedProject, translations, ...props })
        const shouldRemoveRow = getShouldRemoveProjectRowBasedOnContext(
          project,
          appContext,
          userMembershipContextOptions
        )
        if (shouldRemoveRow) {
          grid.AnimateRow(row, "Delete", undefined, () => grid.RemoveRow(row))
        } else {
          syncDataFromServerToGrid(grid, [projectRow])
        }
      })
    }
    return newValue
  }

  window.Grids.OnClick = function (grid, row, col) {
    // Disable header click for open column
    if (col === "open" && row.Kind === "Header") {
      return true
    }
    if (col === "linksAndAttachments") {
      const event = window.event
      const target = event?.target as HTMLElement
      const fileLink = target?.closest("[data-file-id]")
      if (fileLink) {
        const fileId = fileLink.getAttribute("data-file-id")
        if (fileId) {
          openFileInBrowser(fileId)
          return true
        }
      }
    }
    if (col === "open") {
      const isProjectRow = row.Def.Name !== "Group"
      if (isProjectRow) setNavigateTo(paths.projectBasic(row.id))
    }
    if (col === "AddProject") setNavigateTo(paths.newProject())
    if (col === "ZoomFit" && grid) {
      const ganttZoomRange = getGanttVisibleRange({
        startDate: minStartDate,
        endDate: maxEndDate,
      })
      grid.ZoomTo(ganttZoomRange.start, ganttZoomRange.end)
      return true
    }
  }

  // @ts-expect-error
  window.Grids.OnSearchChange = makeOnSearchChange({ translations })
  window.Grids.OnFilterFinish = makeOnFilterFinish({ translations, gridId: "projects" })
  window.Grids.OnAfterSectionResize = makeOnAfterSectionResize({ ganttZoomRange: defaultGanttZoomRange })

  window.Grids.OnGetSortValue = function (
    grid: TGrid,
    row: TRow & TreeGridProject,
    column: ProjectColumnName,
    value: any
  ) {
    if (column === "plannedEndDate" || column === "plannedStartDate") {
      const shouldSortToBottom = !value
      if (shouldSortToBottom) return Number.MAX_SAFE_INTEGER
    }
    return value
  }

  window.Grids.OnRenderFinish = function (grid) {
    if (grid) {
      applyWeekNumberPrefixLabel(commonTranslations)
      grid.ZoomTo(defaultGanttZoomRange.start, defaultGanttZoomRange.end)
    }
  }

  window.Grids.OnZoom = function (_, zoom) {
    zoomRef.current = zoom
  }

  // @ts-ignore
  window.Grids.OnSelectGroupByChange = function (grid: TGrid, groupBy: string) {
    if (grid) {
      const groubByValue = mapTranslationsToGroupBy(translations)[groupBy] as GroupBy
      const Group = mapGroupByToColumns(groubByValue)
      grid.DoGrouping(Group)
    }
  }

  return { layout, projectsView }
}

const getEnrichedProjects = (updatedProjects: ProjectViewModel[], allProjects: ProjectViewModel[]) => {
  // Enrich updated projects with links and files from original data
  // The API response may not include these, so we preserve them from the source
  return updatedProjects.map((updatedProject) => {
    const originalProject = allProjects.find((p) => p.id === updatedProject.id)
    if (originalProject) {
      return {
        ...updatedProject,
        links: originalProject.links || [],
        files: originalProject.files || [],
        canViewProjectAttachments: originalProject.canViewProjectAttachments,
      }
    }
    return updatedProject
  })
}

const ProjectList = (props: ProjectListProps) => {
  const translations = useTranslations()
  const { layout, projectsView } = useProjectList(props)
  const { projects, dateFormat, timeZone, options } = props

  React.useEffect(() => {
    const data = makeTreegridProjectRows({ projects, translations, dateFormat, options, timeZone })
    createTreegrid({ id, layout, data })
    return () => destroyTreegrid(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultVisibleColumns, props.defaultColumnOrder])

  React.useEffect(() => {
    const data = makeTreegridProjectRows({ projects, translations, dateFormat, options, timeZone })
    updateGridData({ id, data })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsView])

  return <div id={id} style={{ height: "calc(100vh - 128px)" }} data-test="project-list-container"></div>
}

export default ProjectList

function adaptValue({ column, newValue, oldValue }: { column: string; newValue: any; oldValue: any }) {
  let adaptedValue: any = newValue
  if (isDateColumn(column)) {
    if (newValue) adaptedValue = new Date(newValue).toISOString()
    else adaptedValue = ""
  }
  if (
    column === "participants" ||
    column === "managers" ||
    column === "suppliers" ||
    column === "workspaces" ||
    column === "customers"
  ) {
    const oldArray: string[] = oldValue.split(";").filter(Boolean)
    const newArray: string[] = newValue.split(";").filter(Boolean)
    const add = newArray.filter((x) => !oldArray.includes(x))
    const remove = oldArray.filter((x) => !newArray.includes(x))
    adaptedValue = { add, remove }
  }
  if (column === "state") {
    adaptedValue = getStateFromStateIcon(newValue)
  }
  if (column === "ganttBarColor") {
    adaptedValue = getColorFromColorHtml(newValue)
  }
  return adaptedValue
}

function isDateColumn(columnName: string) {
  return (
    columnName === "actualEndDate" ||
    columnName === "actualStartDate" ||
    columnName === "plannedEndDate" ||
    columnName === "plannedStartDate"
  )
}

function getShouldRemoveRow(project: ProjectViewModel, projectsView: ProjectsViewOptions) {
  if (projectsView === "all") return false
  if (projectsView === "templates") return false
  if (project.archived) return projectsView !== "archived"
  if (project.isRejected) return projectsView !== "rejected"
  if (project.isCompleted) return projectsView !== "completed"
  if (project.isNotStarted || project.isInProgress) return projectsView !== "currentAndFuture"
  return false
}

type ProjectListProps = {
  projects: ProjectViewModel[]
  canCreateProjects: boolean
  defaultColumnOrder: ProjectColumnName[]
  defaultVisibleColumns: ProjectColumnName[]
  options: Option[]
  gridInfo: string
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  timeZone: TimeZoneType
  weekendDays: DayOfWeek[]
  holidays?: HolidayViewModel[]
  calendarSettingsById: Record<string, CalendarSettingsViewModel>
}
